import type { ProductSearchResult } from "../../../api/product_catalog"

/**
 * Resolve free-typed SKU text to a catalog product. The catalog search endpoint already
 * matches product names, SKU codes, UPC/GTIN identifiers, AND the product aliases created
 * on the Forecast Mappings page — so a name that is "mapped" there resolves here too, and
 * the "cannot receive actuals" warning only fires when the text truly matches nothing.
 */

function normalize(text: string | undefined | null): string {
  return String(text ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
}

/** Exact (normalized) match of `query` against a hit's name, SKU code, identifiers, or aliases. */
export function exact_catalog_match(query: string, hits: ProductSearchResult[]): ProductSearchResult | null {
  const q = normalize(query)
  if (!q) return null
  const matches = hits.filter((hit: ProductSearchResult) => {
    if (normalize(hit.name) === q) return true
    if (hit.sku && normalize(hit.sku) === q) return true
    if ((hit.identifiers || []).some((identifier: string) => normalize(identifier) === q)) return true
    if ((hit.aliases || []).some((alias: string) => normalize(alias) === q)) return true
    return false
  })
  // Ambiguity is not a match — attaching either product would be a guess.
  return matches.length === 1 ? matches[0] : null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function product(overrides: Partial<ProductSearchResult>): ProductSearchResult {
    return {
      id: "p1",
      name: "Lychee Sparkling Water (6-pack)",
      sku: "SZ-004",
      is_pack: false,
      is_sellable: true,
      product_groups: [],
      identifiers: ["840269200328"],
      aliases: ["Lychee 6pk"],
      prices: [],
      ...overrides,
    } as ProductSearchResult
  }

  describe("exact_catalog_match", () => {
    it("matches by name, sku, identifier, or forecast-mapping alias", () => {
      const hits = [product({})]
      expect(exact_catalog_match("lychee sparkling water (6-pack)", hits)?.id).toBe("p1")
      expect(exact_catalog_match("sz-004", hits)?.id).toBe("p1")
      expect(exact_catalog_match("840269200328", hits)?.id).toBe("p1")
      expect(exact_catalog_match("Lychee 6pk", hits)?.id).toBe("p1")
      expect(exact_catalog_match("  Lychee   Sparkling Water (6-pack) ", hits)?.id).toBe("p1")
    })

    it("returns null for partial text, no match, or ambiguity", () => {
      const hits = [product({}), product({ id: "p2", name: "Lychee Sparkling Water (SS)", sku: null as any, identifiers: [], aliases: ["Lychee 6pk"] })]
      expect(exact_catalog_match("Lychee", hits)).toBeNull()
      expect(exact_catalog_match("Dragonfruit", hits)).toBeNull()
      // Both products carry the "Lychee 6pk" alias — ambiguous, so no attach.
      expect(exact_catalog_match("Lychee 6pk", hits)).toBeNull()
      expect(exact_catalog_match("", hits)).toBeNull()
    })
  })
}
